import { ref, computed } from 'vue';
import { updateNadeProjectiles } from '../utils/demoViewer/nadeTrajectory';
import {
  BOMB_PATH, bombCarrier, updateBomb, findBombPlantEvent,
} from '../utils/demoViewer/bombRenderer';
import { updateInfernos } from '../utils/demoViewer/infernoRenderer';
import { updateSmokes } from '../utils/demoViewer/smokeRenderer';
import { updateWeaponFires } from '../utils/demoViewer/weaponFireRenderer';
import { updateExplosions } from '../utils/demoViewer/explosionRenderer';
import { updateDamages } from '../utils/demoViewer/damageRenderer';
import { getObjectOpacity, getPlayerColor } from '../utils/demoViewer/renderingUtils';
import { killsInFeed } from '../utils/demoViewer/killFeed';
import { MAX_FRAMES_PER_REFRESH } from './usePlayback';

export function useRenderer(roundData, mapInfo, canvasContainer, svgWidth, svgHeight, mapConfig, currentRound, roundInfo) {
  const currentFrameRef = ref(null);
  const currentTickRef = ref(null);
  const playDemoRef = ref(null);
  const interpolationProgressRef = ref(null);

  function setRefs(currentFrame, currentTick, playDemo, interpolationProgress) {
    currentFrameRef.value = currentFrame;
    currentTickRef.value = currentTick;
    playDemoRef.value = playDemo;
    interpolationProgressRef.value = interpolationProgress;
  }

  const playerCache = ref(new Map());
  const allPlayersCache = ref(new Set());
  const frameTickMap = ref(new Map());
  const bombEventCache = ref(new Map());
  const bombPlantEvent = ref(null);

  const ctPlayers = ref([]);
  const tPlayers = ref([]);
  const playerStates = ref({});

  const renderedPlayers = ref([]);
  const renderedNades = ref([]);
  const renderedBomb = ref(null);
  const renderedFires = ref([]);
  const renderedSmokes = ref([]);
  const renderedDamages = ref([]);
  const renderedWeaponFires = ref([]);
  const renderedExplosions = ref([]);

  const activeKills = ref([]);
  const roundEnded = ref(false);
  const selectedGrenadeGroupId = ref(null);

  const MAP_UNITS = 1024;

  const pixelsPerMapUnit = computed(() => {
    const w = mapConfig?.value?.width || 0;
    return w > 0 ? w / MAP_UNITS : 1;
  });

  // Map-unit constants
  const PLAYER_RADIUS_U = 12;
  const PLAYER_BLIND_RING_EXTRA_U = 1;
  const PLAYER_BLIND_RING_STROKE_U = 2;

  const TRIANGLE_U = 11;
  const TRIANGLE_OFFSET_U = 22;

  const NAME_FONT_U = 12;
  const NAME_BG_HEIGHT_U = 12;
  const NAME_BG_OFFSET_U = 30;
  const NAME_TEXT_OFFSET_U = 20;
  const NAME_BG_RX_U = 2;
  const NAME_PAD_U = 16;
  const NAME_MIN_W_U = 40;
  const NAME_MAX_W_U = 100;

  const WEAPON_ICON_U = 30;
  const WEAPON_ICON_OFFSET_X_U = 15;
  const WEAPON_ICON_OFFSET_Y_U = 55;

  const NADE_TRAIL_STROKE_U = 1;
  const NADE_DASH_U = 4;
  const NADE_BG_RADIUS_U = 10;
  const NADE_ICON_U = 25;
  const NADE_FALLBACK_RADIUS_U = 8;
  const NADE_FALLBACK_STROKE_U = 1.5;

  const FIRE_STROKE_U = 1;
  const FIRE_TEXT_FONT_U = 14;

  const SMOKE_STROKE_U = 1;
  const SMOKE_TEXT_FONT_U = 14;

  const BOMB_DEFUSE_W_U = 50;
  const BOMB_DEFUSE_H_U = 4;
  const BOMB_DEFUSE_OFFSET_X_U = 25;
  const BOMB_DEFUSE_OFFSET_Y_U = 15;
  const BOMB_DEFUSE_RX_U = 2;

  // About half a player dot wide (the path is ~1400 units across)
  const BOMB_SCALE_U = 0.0075;

  // Derived pixel sizes (svg units)
  const playerRadius = computed(() => PLAYER_RADIUS_U * pixelsPerMapUnit.value);
  const blindedRingExtra = computed(() => PLAYER_BLIND_RING_EXTRA_U * pixelsPerMapUnit.value);
  const blindedRingStrokeWidth = computed(() => PLAYER_BLIND_RING_STROKE_U * pixelsPerMapUnit.value);

  const triangleSideLength = computed(() => TRIANGLE_U * pixelsPerMapUnit.value);
  const triangleOffset = computed(() => TRIANGLE_OFFSET_U * pixelsPerMapUnit.value);

  const nameFontSize = computed(() => NAME_FONT_U * pixelsPerMapUnit.value);
  const nameBgHeight = computed(() => NAME_BG_HEIGHT_U * pixelsPerMapUnit.value);
  const nameBgYOffset = computed(() => NAME_BG_OFFSET_U * pixelsPerMapUnit.value);
  const nameTextYOffset = computed(() => NAME_TEXT_OFFSET_U * pixelsPerMapUnit.value);
  const nameBgRx = computed(() => NAME_BG_RX_U * pixelsPerMapUnit.value);
  const namePadding = computed(() => NAME_PAD_U * pixelsPerMapUnit.value);
  const nameMinWidth = computed(() => NAME_MIN_W_U * pixelsPerMapUnit.value);
  const nameMaxWidth = computed(() => NAME_MAX_W_U * pixelsPerMapUnit.value);

  const weaponIconSize = computed(() => WEAPON_ICON_U * pixelsPerMapUnit.value);
  const weaponIconOffsetX = computed(() => WEAPON_ICON_OFFSET_X_U * pixelsPerMapUnit.value);
  const weaponIconOffsetY = computed(() => WEAPON_ICON_OFFSET_Y_U * pixelsPerMapUnit.value);

  const nadeTrailStrokeWidth = computed(() => NADE_TRAIL_STROKE_U * pixelsPerMapUnit.value);
  const nadeTrailDashArray = computed(() => `${NADE_DASH_U * pixelsPerMapUnit.value},${NADE_DASH_U * pixelsPerMapUnit.value}`);
  const nadeBgRadius = computed(() => NADE_BG_RADIUS_U * pixelsPerMapUnit.value);
  const nadeIconSize = computed(() => NADE_ICON_U * pixelsPerMapUnit.value);
  const nadeFallbackRadius = computed(() => NADE_FALLBACK_RADIUS_U * pixelsPerMapUnit.value);
  const nadeFallbackStrokeWidth = computed(() => NADE_FALLBACK_STROKE_U * pixelsPerMapUnit.value);

  const fireStrokeWidth = computed(() => FIRE_STROKE_U * pixelsPerMapUnit.value);
  const fireTextFontSize = computed(() => FIRE_TEXT_FONT_U * pixelsPerMapUnit.value);

  const smokeStrokeWidth = computed(() => SMOKE_STROKE_U * pixelsPerMapUnit.value);
  const smokeTextFontSize = computed(() => SMOKE_TEXT_FONT_U * pixelsPerMapUnit.value);

  const bombDefuseWidth = computed(() => BOMB_DEFUSE_W_U * pixelsPerMapUnit.value);
  const bombDefuseHeight = computed(() => BOMB_DEFUSE_H_U * pixelsPerMapUnit.value);
  const bombDefuseOffsetX = computed(() => BOMB_DEFUSE_OFFSET_X_U * pixelsPerMapUnit.value);
  const bombDefuseOffsetY = computed(() => BOMB_DEFUSE_OFFSET_Y_U * pixelsPerMapUnit.value);
  const bombDefuseRx = computed(() => BOMB_DEFUSE_RX_U * pixelsPerMapUnit.value);

  const bombScale = computed(() => BOMB_SCALE_U * pixelsPerMapUnit.value);
  const bombPathData = BOMB_PATH;

  function getConvertedCords(x, y) {
    if (x === undefined || y === undefined || !mapInfo.value || !canvasContainer.value) return null;

    const sideLength = canvasContainer.value.offsetWidth * 0.6;
    const topLeftCord = {
      x: svgWidth.value / 2 - sideLength / 2,
      y: svgHeight.value / 2 - sideLength / 2,
    };
    const bottomLeftCord = {
      x: svgWidth.value / 2 + sideLength / 2,
      y: svgHeight.value / 2 + sideLength / 2,
    };

    const xPercent = ((x - mapInfo.value.x_0) / mapInfo.value.scale) / 1024;
    const yPercent = ((y - mapInfo.value.y_0) / mapInfo.value.scale) / 1024;

    const xCord = (bottomLeftCord.x - topLeftCord.x) * xPercent + topLeftCord.x;
    const yCord = (bottomLeftCord.y - topLeftCord.y) * -yPercent + topLeftCord.y;

    return { x: xCord, y: yCord };
  }

  function getTrianglePoints(player) {
    const s = triangleSideLength.value;

    // tip and base endpoints (same intent as your current)
    const p2 = { x: 0, y: 0 }; // tip
    const p1 = { x: s, y: s }; // base right
    const p3 = { x: -s, y: s }; // base left

    // arc curvature:
    // choose an arc radius bigger than half the chord to make it gently curved.
    // if you want it to "feel like a circle", a good default is ~2.2*s to 3.0*s.
    const R = playerRadius.value * 1.6;

    // sweepFlag controls which side of the chord the arc bulges toward.
    // with your coords (base at y=s), sweep=0 usually bulges "down".
    const sweepFlag = 1;

    return [
      `M ${p1.x} ${p1.y}`,
      `Q ${p1.x * 0.35} ${p1.y * 0.35} ${p2.x} ${p2.y}`, // right side (soft curve to tip)
      `Q ${p3.x * 0.35} ${p3.y * 0.35} ${p3.x} ${p3.y}`, // left side (soft curve to base-left)
      // circular arc base from left->right
      `A ${R} ${R} 0 0 ${sweepFlag} ${p1.x} ${p1.y}`,
      'Z',
    ].join(' ');
  }

  function getTriangleTransform(player) {
    const offsetY = triangleOffset.value;
    return `translate(${player.x}, ${player.y - offsetY}) rotate(${player.viewAngle} 0 ${offsetY})`;
  }

  function clearPlayerStates() {
    playerStates.value = {};
  }

  function clearCanvas() {
    renderedPlayers.value = [];
    renderedNades.value = [];
    renderedExplosions.value = [];
    renderedBomb.value = null;
    renderedFires.value = [];
    renderedSmokes.value = [];
    renderedDamages.value = [];
    bombPlantEvent.value = null;
    ctPlayers.value = [];
    tPlayers.value = [];
    clearPlayerStates();
    playerCache.value.clear();
    allPlayersCache.value.clear();
    frameTickMap.value.clear();
    bombEventCache.value.clear();
  }

  function initializeCaches() {
    if (!roundData.value || !roundData.value.frames) return;

    allPlayersCache.value.clear();
    frameTickMap.value.clear();
    playerCache.value.clear();
    bombEventCache.value.clear();

    const { frames } = roundData.value;
    const playerSet = new Set();

    for (let frameIndex = 0; frameIndex < frames.length; frameIndex++) {
      const frame = frames[frameIndex];

      frameTickMap.value.set(frame.tick, frameIndex);

      const allPlayers = frame.ct.players.concat(frame.t.players);
      const playerMap = new Map();

      for (let i = 0; i < allPlayers.length; i++) {
        const player = allPlayers[i];
        const { steamID } = player;
        playerSet.add(steamID);
        playerMap.set(steamID, player);
      }

      playerCache.value.set(frameIndex, playerMap);
    }

    playerSet.forEach((steamID) => allPlayersCache.value.add(steamID));

    if (roundData.value.bombEvents && Array.isArray(roundData.value.bombEvents)) {
      roundData.value.bombEvents.forEach((event) => {
        if (event.event) {
          const eventType = event.event;
          if (!bombEventCache.value.has(eventType)) {
            bombEventCache.value.set(eventType, []);
          }
          bombEventCache.value.get(eventType).push(event);
        }
      });

      bombEventCache.value.forEach((events, eventType) => {
        const sorted = [...events].sort((a, b) => a.tick - b.tick);
        bombEventCache.value.set(eventType, sorted);
      });
    }
  }

  function pushPlayer(player, side) {
    if (side === 'CT') {
      const i = ctPlayers.value.findIndex((_element) => _element.steamID === player.steamID);
      if (i > -1) ctPlayers.value[i] = player;
      else ctPlayers.value.push(player);
    } else {
      const i = tPlayers.value.findIndex((_element) => _element.steamID === player.steamID);
      if (i > -1) tPlayers.value[i] = player;
      else tPlayers.value.push(player);
    }
  }

  function updatePlayers() {
    if (!currentFrameRef.value || !roundData.value || !roundData.value.frames[currentFrameRef.value.value]) return;

    const frame = roundData.value.frames[currentFrameRef.value.value];

    let progress = 0;
    let nextFrame = null;

    if (playDemoRef.value && playDemoRef.value.value && interpolationProgressRef.value && interpolationProgressRef.value.value > 0) {
      progress = interpolationProgressRef.value.value;
      if (currentFrameRef.value.value < roundData.value.frames.length - 1) {
        nextFrame = roundData.value.frames[currentFrameRef.value.value + 1];
      }
    } else {
      const tick = currentTickRef.value ? currentTickRef.value.value : 0;
      const lastProcessedTick = Math.floor(tick / 8) * 8;
      const nextProcessedTick = lastProcessedTick + 8;
      progress = (tick - lastProcessedTick) / 8;
      const nextFrameIndex = frameTickMap.value.get(nextProcessedTick);
      nextFrame = nextFrameIndex !== undefined ? roundData.value.frames[nextFrameIndex] : null;
    }

    const players = frame.ct.players.concat(frame.t.players);
    const currentPlayers = new Set(players.map((p) => p.steamID));

    if (allPlayersCache.value.size > currentPlayers.size) {
      const maxSearchFrames = 64;
      const searchStart = Math.max(0, currentFrameRef.value.value - maxSearchFrames);

      const addedPlayers = new Set(currentPlayers);

      allPlayersCache.value.forEach((steamID) => {
        if (addedPlayers.has(steamID)) return;

        let lastKnownState = null;

        for (let i = currentFrameRef.value.value - 1; i >= searchStart; i--) {
          const framePlayerMap = playerCache.value.get(i);
          if (framePlayerMap) {
            const player = framePlayerMap.get(steamID);
            if (player && player.x !== undefined && player.y !== undefined) {
              lastKnownState = player;
              break;
            }
          }
        }

        if (!lastKnownState) {
          for (let i = searchStart - 1; i >= 0; i--) {
            const framePlayerMap = playerCache.value.get(i);
            if (framePlayerMap) {
              const player = framePlayerMap.get(steamID);
              if (player && player.x !== undefined && player.y !== undefined) {
                lastKnownState = player;
                break;
              }
            }
          }
        }

        if (lastKnownState) {
          players.push(lastKnownState);
          addedPlayers.add(steamID);
        }
      });
    }

    const playerMap = new Map();
    players.forEach((user) => {
      if (user && user.steamID) {
        if (!playerMap.has(user.steamID) || currentPlayers.has(user.steamID)) {
          playerMap.set(user.steamID, user);
        }
      }
    });

    renderedPlayers.value = Array.from(playerMap.values()).map((user) => {
      if (!playerStates.value[user.steamID]) {
        playerStates.value[user.steamID] = {};
      }

      playerStates.value[user.steamID] = {
        ...playerStates.value[user.steamID],
        ...user,
      };

      const currentUser = playerStates.value[user.steamID];
      const nextUser = nextFrame?.ct.players.concat(nextFrame?.t.players).find((p) => p.steamID === user.steamID);

      pushPlayer(currentUser, currentUser.side);

      if (currentUser.x === undefined || currentUser.y === undefined) return null;

      const currentCords = getConvertedCords(currentUser.x, currentUser.y);
      const nextCords = nextUser && nextUser.x !== undefined ? getConvertedCords(nextUser.x, nextUser.y) : null;

      if (!currentCords) return null;

      const cords = nextCords ? {
        x: currentCords.x + (nextCords.x - currentCords.x) * progress,
        y: currentCords.y + (nextCords.y - currentCords.y) * progress,
        z: (currentUser.z !== undefined ? currentUser.z : 0) + ((nextUser?.z !== undefined ? nextUser.z : 0) - (currentUser.z !== undefined ? currentUser.z : 0)) * progress,
      } : {
        ...currentCords,
        z: currentUser.z !== undefined ? currentUser.z : 0,
      };

      const isDead = (currentUser.isAlive === false) || (currentUser.hp === 0);
      const opacity = isDead ? 0.3 : 1.0;

      const nameColor = currentUser.side === 'CT' ? '#687cca' : '#e38617';

      const stableZ = cords.z !== undefined ? Math.round(cords.z * 10) / 10 : 0;

      const fullPlayerName = currentUser.playerName || '';
      const playerName = fullPlayerName.length > 12 ? fullPlayerName.substring(0, 12) : fullPlayerName;
      const fontSize = nameFontSize.value;
      const calculatedWidth = (fontSize * playerName.length * 0.6) + namePadding.value;
      const estimatedNameWidth = Math.max(nameMinWidth.value, Math.min(calculatedWidth, nameMaxWidth.value));

      return {
        steamID: currentUser.steamID,
        x: cords.x,
        y: cords.y,
        z: stableZ,
        color: getPlayerColor(currentUser),
        opacity,
        isDead,
        isBlinded: currentUser.isBlinded,
        name: playerName,
        nameColor,
        nameWidth: estimatedNameWidth,
        viewAngle: currentUser.viewX !== undefined ? (-currentUser.viewX + 90 + 360) % 360 : undefined,
        weaponIcon: currentUser.activeWeapon ? `/img/icons/${currentUser.activeWeapon}.png` : null,
        side: currentUser.side,
      };
    }).filter((p) => p !== null);

    renderedPlayers.value = [...renderedPlayers.value].sort((a, b) => {
      const zA = a.z !== undefined ? a.z : 0;
      const zB = b.z !== undefined ? b.z : 0;
      return zA - zB;
    });
  }

  function getPrimary(player) {
    if (!player.inventory) return null;
    let primary;
    player.inventory.forEach((item) => {
      if (item.weaponClass === 'Rifle') {
        primary = item;
      } else if (
        item.weaponClass !== 'Pistols'
        && item.weaponClass !== 'Grenade'
        && item.weaponClass !== 'Equipment'
      ) {
        primary = item;
      } else if (item.weaponClass === 'Pistols' && !primary) {
        primary = item;
      }
    });
    if (!primary) return player.inventory[0];
    return primary;
  }

  const sortedInventoryCache = new Map();

  function getSortedInventory(inventory) {
    if (!inventory) return [];
    const inventoryHash = JSON.stringify(inventory.map((item) => ({ weaponName: item.weaponName, weaponClass: item.weaponClass })));

    if (sortedInventoryCache.has(inventoryHash)) {
      return sortedInventoryCache.get(inventoryHash);
    }

    const grenadeOrder = ['C4', 'Decoy Grenade', 'Flashbang', 'HE Grenade', 'Molotov', 'Incendiary Grenade', 'Smoke Grenade'];
    const sorted = [...inventory].sort((a, b) => grenadeOrder.indexOf(a.weaponName) - grenadeOrder.indexOf(b.weaponName));
    sortedInventoryCache.set(inventoryHash, sorted);
    return sorted;
  }

  function getGrenadeGroups() {
    if (!roundData.value || !roundData.value.grenades) return [];
    const grenadeGroups = [];
    const { grenades } = roundData.value;
    let lastTick = 0;
    let currentGroup = [];
    for (let i = 0; i < grenades.length; i++) {
      if (lastTick === 0) {
        lastTick = grenades[i].throwTick;
        currentGroup.push(grenades[i]);
      } else {
        if (grenades[i].throwTick - lastTick < 2 * 64) {
          currentGroup.push(grenades[i]);
        } else {
          grenadeGroups.push(currentGroup);
          currentGroup = [];
          currentGroup.push(grenades[i]);
        }
        lastTick = grenades[i].throwTick;
      }
    }
    if (currentGroup.length > 0) {
      grenadeGroups.push(currentGroup);
    }
    return grenadeGroups;
  }

  function getGrenadeThrowerName(grenade) {
    let player;
    if (grenade.throwerSide === 'CT') {
      player = ctPlayers.value.find((player) => player.steamID === grenade.throwerSteamID);
    } else {
      player = tPlayers.value.find((player) => player.steamID === grenade.throwerSteamID);
    }
    return player?.playerName;
  }

  const timelineEvents = computed(() => {
    if (!roundData.value) {
      return {
        kills: [], grenadeGroups: [], plantEvent: null, defuseEvent: null, explodeEvent: null, roundEndEvent: null,
      };
    }

    const events = {
      kills: roundData.value.kills || [],
      grenadeGroups: getGrenadeGroups(),
    };

    const bombDefusedEvents = bombEventCache.value.get('bomb_defused') || [];
    const bombPlantedEvents = bombEventCache.value.get('bomb_planted') || [];
    const bombExplodedEvents = bombEventCache.value.get('bomb_exploded') || [];

    events.defuseEvent = bombDefusedEvents[0];
    events.plantEvent = bombPlantedEvents[0];
    events.explodeEvent = bombExplodedEvents[0];

    if (roundInfo.value && Array.isArray(roundInfo.value)) {
      const roundMatch = roundInfo.value.find((round) => round.round_num === currentRound.value);
      if (roundMatch && roundData.value.endTick) {
        events.roundEndEvent = {
          tick: roundData.value.endTick,
          winning_team: roundMatch.winning_team,
          winning_reason: roundMatch.winning_reason,
        };
      }
    }

    return events;
  });

  const grenadeGroups = computed(() => timelineEvents.value.grenadeGroups);
  const timelineKills = computed(() => timelineEvents.value.kills);

  function getTimelineEvents() {
    return timelineEvents.value;
  }

  function getRoundEndReasonIcon(reason) {
    switch (reason) {
      case 1:
        return 'texplode';
      case 7:
        return 'ctdefuse';
      case 8:
        return 'ctskull';
      case 9:
        return 'tskull';
      case 12:
        return 'cttime';
      default:
        return null;
    }
  }

  function roundWinner(roundNum) {
    if (!roundInfo.value || !Array.isArray(roundInfo.value)) return null;
    const roundMatches = roundInfo.value.filter((round) => round.round_num === roundNum);
    return roundMatches[roundMatches.length - 1]?.winning_team;
  }

  function getTimeOfTick(tick) {
    const rd = roundData.value;
    if (!rd) return 0;

    const freezeEnd = rd.freezeTimeEndTick ?? 0;
    const { endTick } = rd; // guaranteed
    const { endOfficialTick } = rd; // guaranteed

    const plant = timelineEvents.value?.plantEvent || bombPlantEvent.value || null;
    const defuse = timelineEvents.value?.defuseEvent || null;
    const explode = timelineEvents.value?.explodeEvent || null;

    // 1) if round already ended (win decided), show time remaining until official end
    if (tick >= endTick) {
      return (endOfficialTick - tick) / 64;
    }

    // 2) bomb timer while bomb is active (planted but not resolved yet, and before endTick)
    if (plant?.tick != null && tick >= plant.tick) {
      const resolvedTick = Math.min(
        defuse?.tick ?? Infinity,
        explode?.tick ?? Infinity,
        endTick ?? Infinity,
      );

      if (tick < resolvedTick) {
        // countdown to explosion time (40s after plant)
        return (plant.tick + 40 * 64 - tick) / 64;
      }
    }

    // 3) normal round timer (115s after freeze time ends)
    return (freezeEnd + 115 * 64 - tick) / 64;
  }

  function getTickTime(event) {
    if (!roundData.value) return 0;
    const eventTick = event.tick || event.throwTick;
    return (
      ((eventTick - roundData.value.freezeTimeEndTick)
        / (roundData.value.endOfficialTick - roundData.value.freezeTimeEndTick))
      * 100
    );
  }

  function updateKills(newTick, oldTick, currentTick) {
    if (!roundData.value) return;
    activeKills.value = killsInFeed(roundData.value.kills, currentTick);
  }

  function renderAt(frame, tick, previousTick, isPlaying, frameRateVal, svgWidthVal) {
    if (!roundData.value || !roundData.value.frames || frame >= roundData.value.frames.length) {
      return;
    }

    const frameData = roundData.value.frames[frame];
    if (!frameData) return;

    bombPlantEvent.value = findBombPlantEvent(roundData.value, tick);

    if (!isPlaying) {
      updatePlayers();
    }

    const bomb = updateBomb(
      roundData.value,
      frame,
      tick,
      getConvertedCords,
      getObjectOpacity,
      bombPlantEvent.value,
      bombEventCache.value,
    );
    // A carried bomb is drawn on its carrier, not as an icon of its own
    renderedBomb.value = bomb && !bomb.isPlanted ? { ...bomb, carrierSteamID: bombCarrier(playerStates.value) } : bomb;
    renderedFires.value = updateInfernos(
      roundData.value,
      tick,
      getConvertedCords,
      getObjectOpacity,
    );
    renderedNades.value = updateNadeProjectiles(
      roundData.value,
      tick,
      frame,
      frameTickMap.value,
      getConvertedCords,
      getObjectOpacity,
    );
    renderedSmokes.value = updateSmokes(
      roundData.value,
      tick,
      getConvertedCords,
      getObjectOpacity,
      pixelsPerMapUnit.value,
    );
    const existingFires = isPlaying ? renderedWeaponFires.value : [];
    renderedWeaponFires.value = updateWeaponFires(
      roundData.value,
      tick,
      previousTick,
      getConvertedCords,
      pixelsPerMapUnit.value,
      frameRateVal,
      existingFires,
    );
    renderedExplosions.value = updateExplosions(
      roundData.value,
      tick,
      isPlaying && frame - frameTickMap.value.get(previousTick) <= MAX_FRAMES_PER_REFRESH ? previousTick : tick,
      getConvertedCords,
      pixelsPerMapUnit.value,
      frameRateVal,
      svgWidthVal,
      renderedExplosions.value,
    );
    renderedDamages.value = updateDamages(
      roundData.value,
      tick,
      previousTick,
      getConvertedCords,
      pixelsPerMapUnit.value,
      renderedDamages.value,
    );
  }

  const sortedPlayers = computed(() => [...renderedPlayers.value].sort((a, b) => {
    const nameA = (a.name || '').toLowerCase();
    const nameB = (b.name || '').toLowerCase();
    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    return 0;
  }));

  const sortedCTPlayers = computed(() => [...ctPlayers.value].sort((a, b) => (a.playerName || '').localeCompare(b.playerName || '', undefined, { sensitivity: 'base' })));

  const sortedTPlayers = computed(() => [...tPlayers.value].sort((a, b) => (a.playerName || '').localeCompare(b.playerName || '', undefined, { sensitivity: 'base' })));

  const sortedNadesByZ = computed(() => [...renderedNades.value].sort((a, b) => {
    const zA = a.z !== undefined ? a.z : 0;
    const zB = b.z !== undefined ? b.z : 0;
    return zA - zB;
  }));

  return {
    playerCache,
    allPlayersCache,
    frameTickMap,
    bombEventCache,
    bombPlantEvent,
    ctPlayers,
    tPlayers,
    playerStates,
    renderedPlayers,
    renderedNades,
    renderedBomb,
    renderedFires,
    renderedSmokes,
    renderedDamages,
    renderedWeaponFires,
    renderedExplosions,
    activeKills,
    roundEnded,
    selectedGrenadeGroupId,
    pixelsPerMapUnit,
    playerRadius,
    blindedRingExtra,
    blindedRingStrokeWidth,
    bombScale,
    bombPathData,
    nameFontSize,
    nameBgHeight,
    nameBgYOffset,
    nameTextYOffset,
    nameBgRx,
    weaponIconSize,
    weaponIconOffsetX,
    weaponIconOffsetY,
    nadeTrailStrokeWidth,
    nadeTrailDashArray,
    nadeBgRadius,
    nadeIconSize,
    nadeFallbackRadius,
    nadeFallbackStrokeWidth,
    fireStrokeWidth,
    fireTextFontSize,
    smokeStrokeWidth,
    smokeTextFontSize,
    bombDefuseWidth,
    bombDefuseHeight,
    bombDefuseOffsetX,
    bombDefuseOffsetY,
    bombDefuseRx,
    getConvertedCords,
    getTrianglePoints,
    getTriangleTransform,
    clearPlayerStates,
    clearCanvas,
    initializeCaches,
    updatePlayers,
    pushPlayer,
    getPrimary,
    getSortedInventory,
    getGrenadeGroups,
    getGrenadeThrowerName,
    timelineEvents,
    grenadeGroups,
    timelineKills,
    getTimelineEvents,
    getRoundEndReasonIcon,
    roundWinner,
    getTimeOfTick,
    getTickTime,
    updateKills,
    renderAt,
    sortedPlayers,
    sortedCTPlayers,
    sortedTPlayers,
    sortedNadesByZ,
    setRefs,
  };
}
